/**
 * Custom React Flow edge that follows ELK-computed bend points.
 *
 * When ELK's orthogonal router is enabled, each edge gets a sequence of
 * waypoints that route around nodes. This component renders those
 * waypoints as a smooth path with rounded corners at each bend.
 */

import { memo } from "react";
import { BaseEdge, type EdgeProps, type Edge } from "@xyflow/react";
import type { ElkPoint } from "./dagLayout";

interface ElkRoutedEdgeData {
  elkPoints?: ElkPoint[];
  [key: string]: unknown;
}

const CORNER_RADIUS = 8;

function samePoint(a: ElkPoint | undefined, b: ElkPoint | undefined): boolean {
  return !!a && !!b && a.x === b.x && a.y === b.y;
}

function pushUnique(points: ElkPoint[], point: ElkPoint | undefined) {
  if (!point || samePoint(points[points.length - 1], point)) return;
  points.push(point);
}

export function buildAnchoredRoute(
  points: ElkPoint[],
  sourceX: number,
  sourceY: number,
  targetX: number,
  targetY: number,
): ElkPoint[] {
  if (points.length < 2) {
    return [
      { x: sourceX, y: sourceY },
      { x: targetX, y: targetY },
    ];
  }

  const anchored: ElkPoint[] = [];
  const source = { x: sourceX, y: sourceY };
  const target = { x: targetX, y: targetY };
  const sourceStart = points[0]!;
  const sourceNext = points[1]!;
  const targetPrev = points[points.length - 2]!;
  const targetEnd = points[points.length - 1]!;
  const sourceSegmentVertical =
    Math.abs(sourceNext.y - sourceStart.y) >= Math.abs(sourceNext.x - sourceStart.x);
  const targetSegmentVertical =
    Math.abs(targetEnd.y - targetPrev.y) >= Math.abs(targetEnd.x - targetPrev.x);

  pushUnique(anchored, source);
  pushUnique(
    anchored,
    sourceSegmentVertical
      ? { x: sourceStart.x, y: sourceY }
      : { x: sourceX, y: sourceStart.y },
  );

  for (const point of points.slice(1, -1)) {
    pushUnique(anchored, point);
  }

  pushUnique(
    anchored,
    targetSegmentVertical
      ? { x: targetEnd.x, y: targetY }
      : { x: targetX, y: targetEnd.y },
  );
  pushUnique(anchored, target);
  return anchored;
}

function pointsToSmoothPath(points: ElkPoint[]): string {
  const p0 = points[0]!;
  const p1 = points[1]!;
  if (points.length === 2) {
    return `M ${p0.x} ${p0.y} L ${p1.x} ${p1.y}`;
  }

  let path = `M ${p0.x} ${p0.y}`;

  for (let i = 1; i < points.length - 1; i++) {
    const prev = points[i - 1]!;
    const curr = points[i]!;
    const next = points[i + 1]!;

    const dx1 = prev.x - curr.x;
    const dy1 = prev.y - curr.y;
    const len1 = Math.hypot(dx1, dy1);

    const dx2 = next.x - curr.x;
    const dy2 = next.y - curr.y;
    const len2 = Math.hypot(dx2, dy2);

    if (len1 === 0 || len2 === 0) {
      path += ` L ${curr.x} ${curr.y}`;
      continue;
    }

    const r = Math.min(CORNER_RADIUS, len1 / 2, len2 / 2);

    const p1x = curr.x + (dx1 / len1) * r;
    const p1y = curr.y + (dy1 / len1) * r;
    const p2x = curr.x + (dx2 / len2) * r;
    const p2y = curr.y + (dy2 / len2) * r;

    path += ` L ${p1x} ${p1y} Q ${curr.x} ${curr.y} ${p2x} ${p2y}`;
  }

  const last = points[points.length - 1]!;
  path += ` L ${last.x} ${last.y}`;
  return path;
}

function midpoint(points: ElkPoint[]): ElkPoint {
  const totalLen: number[] = [0];
  for (let i = 1; i < points.length; i++) {
    const pi = points[i]!;
    const pPrev = points[i - 1]!;
    totalLen.push(
      totalLen[i - 1]! + Math.hypot(pi.x - pPrev.x, pi.y - pPrev.y),
    );
  }
  const half = totalLen[totalLen.length - 1]! / 2;
  for (let i = 1; i < totalLen.length; i++) {
    if (totalLen[i]! >= half) {
      const segLen = totalLen[i]! - totalLen[i - 1]!;
      const pi = points[i]!;
      const pPrev = points[i - 1]!;
      const t = segLen === 0 ? 0 : (half - totalLen[i - 1]!) / segLen;
      return {
        x: pPrev.x + (pi.x - pPrev.x) * t,
        y: pPrev.y + (pi.y - pPrev.y) * t,
      };
    }
  }
  return points[Math.floor(points.length / 2)]!;
}

export const ElkRoutedEdge = memo(function ElkRoutedEdge({
  data,
  style,
  markerEnd,
  markerStart,
  sourceX,
  sourceY,
  targetX,
  targetY,
  label,
}: EdgeProps<Edge<ElkRoutedEdgeData>>) {
  const points = data?.elkPoints;

  if (!points || points.length < 2) {
    return (
      <BaseEdge
        path={`M ${sourceX} ${sourceY} L ${targetX} ${targetY}`}
        style={style}
        markerEnd={markerEnd}
        markerStart={markerStart}
        labelX={(sourceX + targetX) / 2}
        labelY={(sourceY + targetY) / 2}
        label={label}
      />
    );
  }

  const adjusted = buildAnchoredRoute(points, sourceX, sourceY, targetX, targetY);

  const mid = midpoint(adjusted);
  return (
    <BaseEdge
      path={pointsToSmoothPath(adjusted)}
      style={style}
      markerEnd={markerEnd}
      markerStart={markerStart}
      labelX={mid.x}
      labelY={mid.y}
      label={label}
    />
  );
});
